import { Link, router, usePage } from '@inertiajs/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { SharedProps } from '../types'
import type { ToolMenuItem } from '../types/tool'
import { expandInputCenter, useInputCenter } from './InputCenter/inputCenterStore'
import { useLauncher } from './Agent/launcherStore'
import { IS_TOUCH, MOD_KEY } from '../lib/platform'
import Ado from './Ado'
import NotificationBell from './NotificationBell'
import { openShortcuts } from './InputCenter/shortcutsEvent'
import { rememberPage } from '../lib/lastPage'
import WhatsNew, { openWhatsNew } from './WhatsNew'

// match：高亮判定用的前缀，默认就是 href。/todo 的五个视图各自一个地址
// （/todo/today、/todo/backlog…），href 指向默认视图，高亮要按 /todo 判，
// 否则访问 /todo/backlog 时这一项不会亮。
type NavLeaf = { href: string; label: string; match?: string }

// 一块功能。
//
// **顶栏现在就是首页讲的那五块，顺序也是闭环的顺序**：
// 我要做什么 → 实际花了多久 → agent 做了什么 → 我们共同知道什么 → agent 做出了什么。
//
// 原来是七个顶级项（待办 / 番茄钟 / 规划▾ / 统计▾ / 日志 / wiki / artifact），
// 而首页讲的是五块 —— 两边对不上。「规划」里的计划、项目、AI Tasker 都是在组织
// 「要做什么」，归待办；「统计」里的仪表盘、洞察、日历、历史都是在看
// 「实际花了多久」，归番茄钟。收完正好是五块。
//
// **但光是收起来只是把东西藏深一层，不等于用户能理解。** 所以每一块都带
// role 和 by：下拉打开时先说这一块在闭环里回答什么问题、以及谁在写它。
// 「谁在写」是这个产品的核心区别（人和 agent 在同一份数据上干活），
// 导航本身说一遍，比指望每个人都读过首页可靠。
type NavBlock = NavLeaf & {
  role: string            // 这一块回答的问题
  by: string              // 谁在写
  items?: NavLeaf[]       // 同一块下的其它页面
}

// 一项都没删：顶栏是这些页面的唯一入口（除 /worklog 那一组页面之间互相有
// 链接外，全前端再没有指向它们的 href），删掉就等于让页面失联。
// frontend_nav_coverage_test 守着这条。
const NAV_AUTH: NavBlock[] = [
  {
    href: '/todo/today',
    label: '待办',
    match: '/todo',
    role: '我要做什么',
    by: '你写为主，agent 可增可改',
    items: [
      { href: '/tracker/plan', label: '计划' },
      { href: '/tracker/projects', label: '项目' },
      { href: '/ai-tasker', label: 'AI Tasker' },
    ],
  },
  {
    href: '/pomodoro',
    label: '番茄钟',
    role: '实际花了多久',
    by: '你按开始，自动记录',
    items: [
      { href: '/dashboard', label: '仪表盘' },
      { href: '/insights', label: '数据洞察' },
      { href: '/calendar', label: '日历' },
      { href: '/history', label: '历史记录' },
    ],
  },
  { href: '/worklog', label: 'AI工作日志', role: 'agent 做了什么', by: 'agent 写，改不了也删不掉' },
  { href: '/wiki', label: 'wiki', role: '我们共同知道什么', by: 'agent 为主，你可读可删' },
  { href: '/artifacts', label: 'artifact', role: 'agent 做出了什么', by: 'agent 写，分享由你决定' },
  // Not a step of the loop: where what the loop made is passed around
  // (Tools Explore PRD 2026-10-19 §7.1). Right after artifact, because
  // that is what it grows out of.
  { href: '/explore', label: 'Explore', role: '大家做出了什么', by: '大家公开，你挑来用' },
]

// 未登录能看的两页。番茄钟是 SEO 落地页，不能藏进下拉；/developers 是接入
// 文档，公开的意义就在于能分享、能被搜到，藏在登录后两样都做不到。
const NAV_PUBLIC: NavLeaf[] = [
  { href: '/pomodoro', label: '番茄钟' },
  { href: '/developers', label: '接入' },
]

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`ml-1 h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

// 自足的下拉：不引 antd。Header 挂在每个页面上（含 SEO 落地页 /），现在是
// 零 antd 依赖，不值得为一个下拉把整个组件库拖进首屏。
//
// Inertia 的 layout 在同类型页面之间跳转时不会卸载（AppLayout 一直挂着），
// 所以点了里面的链接必须自己收起来，否则跳到新页面下拉还开着。面板里只有
// 链接，所以在面板上兜一个 onClick 就够，不用逐个链接挂。
function NavDropdown({
  label,
  active,
  align = 'left',
  children,
}: {
  label: string
  active: boolean
  align?: 'left' | 'right'
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
          active
            ? 'border-b-2 border-brand-primary text-brand-ink'
            : 'border-b-2 border-transparent text-brand-ink2 hover:border-brand-line2 hover:text-brand-ink'
        }`}
      >
        <span className="max-w-[14ch] truncate">{label}</span>
        <Chevron open={open} />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className={`brand-float absolute top-full z-20 w-56 rounded-xl py-1 shadow-brandLg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {children}
        </div>
      )}
    </div>
  )
}

// 一块功能的顶栏入口。
//
// **标签本身是链接，箭头才是展开。** 把整块做成「点一下展开下拉」会让最常用的
// 待办从一次点击变成两次 —— 为了解释闭环而让每天用的路径变长，不划算。
//
// 下拉的第一行不是链接，是这一块在闭环里回答的问题 + 谁在写它。那是用户真正
// 需要知道的事：这个产品的区别就在于人和 agent 写同一份数据，而哪一块归谁写
// 各不相同（日志 agent 改不了，番茄钟 agent 根本没有工具）。
function NavBlockMenu({
  block,
  active,
  isActive,
}: {
  block: NavBlock
  active: boolean
  isActive: (href: string, match?: string) => boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const line = active
    ? 'border-b-2 border-brand-primary text-brand-ink'
    : 'border-b-2 border-transparent text-brand-ink2 hover:border-brand-line2 hover:text-brand-ink'

  return (
    <div ref={ref} className="relative inline-flex items-center">
      <Link
        href={block.href}
        title={`${block.role} · ${block.by}`}
        className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${line}`}
      >
        {block.label}
      </Link>

      {block.items && (
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={`${block.label}的其它页面`}
          onClick={() => setOpen((o) => !o)}
          className={`-ml-0.5 rounded p-0.5 pt-1 ${active ? 'text-brand-ink' : 'text-brand-ink3 hover:text-brand-ink'}`}
        >
          <Chevron open={open} />
        </button>
      )}

      {open && block.items && (
        <div
          onClick={() => setOpen(false)}
          className="brand-float absolute left-0 top-full z-20 w-52 rounded-xl py-1 shadow-brandLg"
        >
          <div className="border-b border-brand-line2 px-4 pb-2 pt-2">
            <div className="font-mono text-[11px] uppercase tracking-widest text-brand-primaryStrong">
              {block.role}
            </div>
            <div className="mt-1 text-[11.5px] leading-snug text-brand-ink3">{block.by}</div>
          </div>
          <Link
            href={block.href}
            className={`block px-4 py-2 text-sm ${
              isActive(block.href, block.match)
                ? 'bg-brand-primarySoft font-medium text-brand-primaryDeep'
                : 'text-brand-ink2 hover:bg-brand-glass2 hover:text-brand-ink'
            }`}
          >
            {block.label}
          </Link>
          {block.items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`block px-4 py-2 text-sm ${
                isActive(item.href, item.match)
                  ? 'bg-brand-primarySoft font-medium text-brand-primaryDeep'
                  : 'text-brand-ink2 hover:bg-brand-glass2 hover:text-brand-ink'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// 「我的」(tools spec 2026-10-18 §8.2): the tools this person saved from
// their artifacts. This is the part of the header that differs per person —
// the five blocks are the product, this one is what each person grew on it.
function ToolsMenu({ tools, path }: { tools: ToolMenuItem[]; path: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const active = path.startsWith('/t/')

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-flex items-center">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
          active
            ? 'border-b-2 border-brand-primary text-brand-ink'
            : 'border-b-2 border-transparent text-brand-ink2 hover:border-brand-line2 hover:text-brand-ink'
        }`}
      >
        我的<span className="ml-1 text-xs text-brand-ink3">{tools.length}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="brand-float absolute left-0 top-full z-20 w-60 rounded-xl py-1 shadow-brandLg"
        >
          <div className="border-b border-brand-line2 px-4 pb-2 pt-2">
            <div className="font-mono text-[11px] uppercase tracking-widest text-brand-primaryStrong">我的 Recipe</div>
            <div className="mt-1 text-[11.5px] leading-snug text-brand-ink3">你存的和你添加的，打开看到的都是你自己的数据</div>
          </div>
          {tools.map((tool) => (
              <Link
                key={tool.tool_key}
                href={`/t/${tool.tool_key}`}
                className={`flex items-center gap-2 px-4 py-2 text-sm ${
                  path === `/t/${tool.tool_key}`
                    ? 'bg-brand-primarySoft font-medium text-brand-primaryDeep'
                    : 'text-brand-ink2 hover:bg-brand-glass2 hover:text-brand-ink'
                } ${tool.taken_down ? 'opacity-50' : ''}`}
              >
                <span className="w-5 text-center">{tool.icon}</span>
                <span className="truncate">{tool.name}</span>
                {/* Someone else's tool: the first character of its creator. */}
                {tool.creator && (
                  <span className="ml-auto shrink-0 text-[11px] text-brand-ink3" title={`由 ${tool.creator} 创建`}>
                    {tool.taken_down ? '已停用' : tool.creator.slice(0, 1)}
                  </span>
                )}
              </Link>
            ))}
          <div className="mt-1 border-t border-brand-line2 pt-1">
            <Link href="/explore" className="block px-4 py-2 text-sm text-brand-primaryStrong hover:bg-brand-glass2">
              去 Explore 找更多 Recipe →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

// 输入条收起态就是这枚入口（spec docs/design/input-center-collapse-mockup.html §03）。
//
// 三档宽度（§06）：顶栏右边不是空地 —— 量过，全态要 1069px 才摆得下，而顶栏
// 今天没有这枚入口时就已经要 846px 了。所以 xl 起带文案、lg 起带键帽、更窄只剩图标。
// 触屏永远只剩图标：那台设备没有这个键，印着键帽是假信息。
//
// 展开时它 invisible 而不是 hidden：位置留着，顶栏其它项才不会跟着左右跳一下。
function InputCenterEntry({ compact = false }: { compact?: boolean }) {
  const { expanded, pinned } = useInputCenter()
  // The collapsed bar hides its ✦ AI助手 button, and with it the dot for a
  // scheduled result nobody opened (scheduled tasks design §3.3): the dot
  // moves here. Not when the user hid the AI button themselves.
  const { unread, hidden, panelOpen } = useLauncher()
  const dot = unread && !hidden && !panelOpen
  const away = expanded || pinned
  const label = '写一件事，或者问一句'
  return (
    <button
      type="button"
      onClick={expandInputCenter}
      aria-label={dot ? `${label}（AI助手有定时任务的新结果）` : label}
      title={IS_TOUCH ? label : `${label}（${MOD_KEY}K）`}
      className={`relative inline-flex flex-none items-center gap-2 rounded-full border border-brand-line bg-brand-glass2 text-brand-ink3 hover:border-brand-primary hover:bg-brand-primarySoft hover:text-brand-ink2 ${
        compact ? 'h-8 w-8 justify-center' : 'h-8 px-2.5'
      } ${away ? 'invisible' : ''}`}
    >
      {/* 阿豆，睡着。实心珊瑚＋奶油，**不跟 currentColor 走** —— hover 变的是
          底色、边框、文案和键帽，图标本身不参与。 */}
      <Ado state="sleep" size={15} />
      {!compact && <span className="hidden xl:inline whitespace-nowrap text-[12.5px]">{label}</span>}
      {!compact && !IS_TOUCH && (
        <kbd className="hidden lg:inline-flex items-center rounded border border-brand-line bg-brand-glass px-1 font-mono text-[11px]">
          {MOD_KEY}K
        </kbd>
      )}
      {dot && <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-todo-urgent" />}
    </button>
  )
}

export default function Header() {
  const { props, url } = usePage<SharedProps>()
  const user = props.auth?.user
  const isAuthenticated = !!user
  const [mobileOpen, setMobileOpen] = useState(false)

  const path = url.split('?')[0]
  // A tool page has no header; its 「← asaptodo」 returns here.
  useEffect(() => {
    if (isAuthenticated) rememberPage(url)
  }, [url, isAuthenticated])
  const isActive = (href: string, match?: string) => {
    const prefix = match ?? href
    return prefix === '/' ? path === '/' : path.startsWith(prefix)
  }
  // 一块亮不亮：主入口或它下面任何一页命中都算 —— 在 /insights 上时
  //「番茄钟」该是亮的，否则用户看不出自己在哪一块里。
  const blockActive = (b: NavBlock) =>
    isActive(b.href, b.match) || (b.items ?? []).some((i) => isActive(i.href, i.match))

  const nav = isAuthenticated ? NAV_AUTH : NAV_PUBLIC
  const closeMobile = () => setMobileOpen(false)
  const logout = () => router.delete('/logout')

  const desktopLink = ({ href, label, match }: NavLeaf) => (
    <Link
      key={href}
      href={href}
      className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
        isActive(href, match)
          ? 'border-b-2 border-brand-primary text-brand-ink'
          : 'border-b-2 border-transparent text-brand-ink2 hover:border-brand-line2 hover:text-brand-ink'
      }`}
    >
      {label}
    </Link>
  )

  const menuLink = ({ href, label, match }: NavLeaf) => (
    <Link
      key={href}
      href={href}
      className={`block px-4 py-2 text-sm ${
        isActive(href, match)
          ? 'bg-brand-primarySoft font-medium text-brand-primaryDeep'
          : 'text-brand-ink2 hover:bg-brand-glass2 hover:text-brand-ink'
      }`}
    >
      {label}
    </Link>
  )
  const menuButton = 'px-4 py-2 text-left text-sm text-brand-ink2 hover:bg-brand-glass2 hover:text-brand-ink'

  // 移动端菜单同样要在跳转后收起 —— layout 不卸载，状态会留着。
  const mobileLink = ({ href, label, match }: NavLeaf, indented = false) => (
    <Link
      key={href}
      href={href}
      onClick={closeMobile}
      className={`block py-2 pr-4 text-base font-medium ${indented ? 'pl-8' : 'pl-3'} ${
        isActive(href, match)
          ? 'border-l-4 border-brand-primary bg-brand-primarySoft text-brand-primaryDeep'
          : 'border-l-4 border-transparent text-brand-ink2 hover:border-brand-line2 hover:bg-brand-glass2 hover:text-brand-ink'
      }`}
    >
      {label}
    </Link>
  )

  return (
    <header className="brand-glass sticky top-0 z-40 border-x-0 border-t-0">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {isAuthenticated && <WhatsNew />}
        <div className="flex h-16 justify-between">
          <div className="flex">
            <div className="flex flex-shrink-0 items-center">
              <Link href="/" className="font-display text-xl font-bold tracking-tight text-brand-primaryStrong">
                asaptodo
              </Link>
            </div>
            <div className="hidden min-w-0 items-center sm:ml-6 sm:flex sm:gap-6">
              {isAuthenticated
                ? NAV_AUTH.map((b, i) => (
                    <Fragment key={b.href}>
                      {/* 前两块是你写的，后三块是 agent 写的 —— 这条缝是闭环在
                          顶栏上唯一看得见的痕迹，比把五项平铺更说明问题。 */}
                      {i === 2 && <span aria-hidden="true" className="h-4 w-px bg-brand-line" />}
                      <NavBlockMenu block={b} active={blockActive(b)} isActive={isActive} />
                    </Fragment>
                  ))
                : NAV_PUBLIC.map(desktopLink)}
              {/* Only once there is a tool: someone who never saved one sees
                  the header exactly as before. */}
              {isAuthenticated && (props.tools ?? []).length > 0 && (
                <>
                  <span aria-hidden="true" className="h-4 w-px bg-brand-line" />
                  <ToolsMenu tools={props.tools ?? []} path={path} />
                </>
              )}
            </div>
          </div>

          <div className="hidden items-center gap-4 sm:ml-6 sm:flex">
            {isAuthenticated && <InputCenterEntry />}
            {/* 被 @ 到的通知（PRD 2026-10-31-mentions §7）。放在账户菜单左边
                而不是菜单里面：有未读时那个红点要不点开就看得见。 */}
            {isAuthenticated && <NotificationBell />}
            {isAuthenticated ? (
              <NavDropdown
                label={user?.name || user?.email || '账户'}
                active={isActive('/settings') || isActive('/developers')}
                align="right"
              >
                {/* 登录的是哪个账号。只是标识、不可点 —— 下面一行就是设置。
                    没填显示名时只剩邮箱一行，不重复。 */}
                <div className="mb-1 border-b border-brand-line2 px-4 pb-2.5 pt-2">
                  <div className="truncate text-sm font-medium text-brand-ink">{user?.name || user?.email}</div>
                  {user?.name && (
                    <div className="mt-px truncate text-xs text-brand-ink3" title={user.email}>
                      {user.email}
                    </div>
                  )}
                </div>
                {/* 三组：去哪儿 / 看说明 / 退出。两个界面开关（输入条常驻、AI助手按钮）
                    在设置页的「界面」一节 —— 放这里点完菜单就收起，看不见是开是关。
                    spec: docs/design/account-menu-mockup.html */}
                {menuLink({ href: '/settings', label: '设置' })}
                {menuLink({ href: '/developers', label: '接入文档' })}
                {user?.admin && menuLink({ href: '/admin', label: '后台' })}
                {user?.virtual_office && menuLink({ href: '/virtual_office', label: '办公室' })}
                <div aria-hidden="true" className="my-1 h-px bg-brand-line2" />
                {/* The input bar's sheet (input-center PRD §27 S2): the way in
                    for people who do not know the ? key, so the key is printed. */}
                <button type="button" onClick={openShortcuts} className={`flex w-full items-center ${menuButton}`}>
                  快捷键和写法
                  {!IS_TOUCH && (
                    <kbd className="ml-auto rounded border border-brand-line bg-brand-glass px-1 font-mono text-[11px] text-brand-ink3">
                      ?
                    </kbd>
                  )}
                </button>
                {/* 重看新功能卡片：登录后弹出的那组，错过了从这里补。 */}
                <button type="button" onClick={openWhatsNew} className={`block w-full ${menuButton}`}>
                  新功能
                </button>
                <div aria-hidden="true" className="my-1 h-px bg-brand-line2" />
                <button type="button" onClick={logout} className={`block w-full ${menuButton}`}>
                  退出
                </button>
              </NavDropdown>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  href="/login"
                  className="inline-flex items-center rounded-full border border-brand-line bg-brand-glass2 px-4 py-1.5 text-xs font-semibold text-brand-ink hover:bg-brand-glass focus:outline-none focus:ring-2 focus:ring-brand-clash focus:ring-offset-2"
                >
                  登录
                </Link>
                <Link
                  href="/register"
                  className="inline-flex items-center rounded-full border border-transparent bg-brand-primaryStrong px-4 py-1.5 text-xs font-semibold text-brand-onPrimary shadow-brand hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-brand-clash focus:ring-offset-2"
                >
                  注册
                </Link>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:hidden">
            {isAuthenticated && <InputCenterEntry compact />}
            {/* 窄屏也放外面，不塞进汉堡菜单：未读红点藏在菜单里等于没有。 */}
            {isAuthenticated && <NotificationBell />}
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg p-2 text-brand-ink3 hover:bg-brand-glass2 hover:text-brand-ink focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-clash"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            >
              <span className="sr-only">打开菜单</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={mobileOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 窄屏放不下下拉，改成分节列表：每块的主入口一行，下面跟一行它在闭环里
          回答什么、谁在写，再把同块的其它页面缩进一级。 */}
      <div className={`sm:hidden ${mobileOpen ? 'block' : 'hidden'}`}>
        <div className="space-y-1 pb-3 pt-2">
          {isAuthenticated
            ? NAV_AUTH.map((b) => (
                <div key={b.href}>
                  {mobileLink(b)}
                  {/* 窄屏放得下，就把「这一块回答什么」直接写出来，不藏在下拉里 */}
                  <div className="px-3 pb-1 pl-[1.1rem] font-mono text-[11px] text-brand-ink3">
                    {b.role} · {b.by}
                  </div>
                  {(b.items ?? []).map((item) => mobileLink(item, true))}
                </div>
              ))
            : NAV_PUBLIC.map((item) => mobileLink(item))}

          {isAuthenticated && (props.tools ?? []).length > 0 && (
            <div>
              <div className="px-3 pb-1 pl-[1.1rem] pt-2 font-mono text-[11px] text-brand-ink3">我的 Recipe</div>
              {(props.tools ?? []).map((tool) => (
                <Link
                  key={tool.tool_key}
                  href={`/t/${tool.tool_key}`}
                  onClick={closeMobile}
                  className="block border-l-4 border-transparent py-2 pl-8 pr-4 text-base font-medium text-brand-ink2 hover:bg-brand-glass2"
                >
                  {tool.icon} {tool.name}
                </Link>
              ))}
            </div>
          )}

          {isAuthenticated ? (
            <div className="border-t border-brand-line pb-3 pt-4">
              <div className="min-w-0 px-4">
                <div className="truncate text-base font-medium text-brand-ink">{user?.name || user?.email}</div>
                {user?.name && <div className="truncate text-sm text-brand-ink3">{user.email}</div>}
              </div>
              {/* 与桌面那份同样三组。「后台」原来窄屏漏了。 */}
              <div className="mt-3 space-y-1">
                {mobileLink({ href: '/settings', label: '设置' })}
                {mobileLink({ href: '/developers', label: '接入文档' })}
                {user?.admin && mobileLink({ href: '/admin', label: '后台' })}
                {user?.virtual_office && mobileLink({ href: '/virtual_office', label: '办公室' })}
                <div aria-hidden="true" className="mx-4 h-px bg-brand-line2" />
                <button
                  type="button"
                  onClick={() => {
                    closeMobile()
                    openShortcuts()
                  }}
                  className="block w-full px-4 py-2 text-left text-base font-medium text-brand-ink2 hover:bg-brand-glass2"
                >
                  写法说明
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeMobile()
                    openWhatsNew()
                  }}
                  className="block w-full px-4 py-2 text-left text-base font-medium text-brand-ink2 hover:bg-brand-glass2"
                >
                  新功能
                </button>
                <div aria-hidden="true" className="mx-4 h-px bg-brand-line2" />
                <button
                  type="button"
                  onClick={logout}
                  className="block w-full px-4 py-2 text-left text-base font-medium text-brand-ink2 hover:bg-brand-glass2"
                >
                  退出
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-1 border-t border-brand-line pb-3 pt-4">
              {mobileLink({ href: '/login', label: '登录' })}
              {mobileLink({ href: '/register', label: '注册' })}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
